/**
 * Log-odds Event Engine v0. Hand weights — uncalibrated until a control set says otherwise.
 * Daily FWI is a gate. The hour (VPD / gust / afternoon) is the prior.
 * Confirmed still requires a thermal SensorObservationGroup.
 * MTG 10-min slots count as one stare (best slot + persist), not n independent votes.
 * Recurring modest heat across ≥4 days cannot reach Likely / Observed (standing stacks).
 */

import { classifyEventStatus, EVENT_THRESHOLDS, POLAR_NOMINAL_MW, POLAR_STRONG_MW, STANDING_MIN_DAYS } from "./event-state";
import { saneFwi } from "./fwi";
import { groupObservations } from "./observe";
import { IMPORTANT_FRP_MW, IMPORTANT_MIN_DETECTIONS } from "./status";
import type {
  Detection,
  EventContext,
  EventScore,
  EvidenceLine,
  EvidenceStrength,
  Incident,
  SensorObservationGroup,
} from "./types";

/** Named so replay can override a copy without editing live ingest. */
export const SCORE_WEIGHTS = {
  /** sigmoid(-1.6) ≈ 0.17 — below Suspected before any thermal. */
  prior_base: -1.6,
  /** Logged, not a vote — two pinpricks must not add up to Suspected. */
  thermal_trace: 0,
  thermal_weak: 2.0,
  thermal_nominal: 2.4,
  thermal_strong: 2.8,
  /** MTG is 10-min geostationary, not a polar overpass — stay below Likely on the first slot. */
  mtg_weak: 1.2,
  mtg_nominal: 1.6,
  mtg_strong: 2.0,
  persist_10m: 1.0,
  persist_30m: 0.7,
  independent_sensor: 1.5,
  extra_sensor: 1.0,
  frp_growth: 0.6,
  spatial_growth: 0.3,
  fwi_extreme: 0.3,
  dry: 0.2,
  wind: 0.1,
  near_human: 0.15,
  /** This hour is the driest/hottest of the lookback — only after the dry gate. */
  hour_vpd_peak: 0.45,
  hour_rh_dry: 0.25,
  hour_gust: 0.35,
  hour_afternoon: 0.2,
  fuel_fine: 0.1,
  sunglint: -1.0,
  /** Unconfirmed + silent. Polar gaps of a few hours are normal; half a day is not. */
  stale_6h: -1.0,
  stale_12h: -2.4,
  static_hot: -3.5,
  water: -4.0,
  industrial_fuel: -2.5,
} as const;

/** No pixel and no peak hour — daily drought alone cannot yellow the map. */
const NO_THERMAL_DAY_CAP = EVENT_THRESHOLDS.suspected - 0.01;

export function sigmoid(x: number): number {
  if (x >= 20) return 1;
  if (x <= -20) return 0;
  return 1 / (1 + Math.exp(-x));
}

export function logitFromP(p: number): number {
  const e = Math.min(1 - 1e-6, Math.max(1e-6, p));
  return Math.log(e / (1 - e));
}

function line(
  key: string,
  label: string,
  delta: number,
  strength: EvidenceStrength,
): EvidenceLine {
  // Prior is the starting odds, not a vote against — keep it off the red minus column.
  const polarity = key === "prior" ? "prior" : delta < 0 ? "against" : "for";
  return { key, label, polarity, strength, delta_logit: delta };
}

/** EFFIS "very high" / bone-dry fine fuel — without this, hourly peaks stay off. */
export function dryEnough(ctx: EventContext): boolean {
  const fwi = saneFwi(ctx.fwi);
  return (
    (fwi != null && fwi >= 38) ||
    (ctx.ffmc != null && ctx.ffmc >= 90) ||
    (ctx.hours_since_rain != null && ctx.hours_since_rain >= 72)
  );
}

export type ThermalTier = "trace" | "weak" | "nominal" | "strong";

/**
 * Weak / nominal / strong from the sensor, not P(wildfire).
 * FIRMS / MTG confidence flags are quality, not a vote — do not promote on them.
 */
export function thermalStrength(g: SensorObservationGroup): ThermalTier {
  if (g.platform === "MTG") {
    const dt = g.bt_diff_max ?? 0;
    const sza = g.sza_min;
    const glint = sza != null && sza < 25;
    // High-sun + modest ΔT is the sunglint / hot-ground shape.
    // Ingest already dropped MTG below 20 MW — do not require FRP < 20 or this branch is dead.
    if (glint && dt < 6) return "weak";
    if (g.frp_max >= 80 || (dt >= 8 && !glint)) return "strong";
    if (g.frp_max >= 15 || dt >= 5) return "nominal";
    return "weak";
  }
  // EFFIS S3 points have no FRP. A located, timed hit is a weak second look —
  // enough to help Observed, not enough to invent a fire by itself.
  if (g.platform === "S3") return "weak";
  // Polar size is FRP. Pixel count is not a second ladder.
  if (g.frp_max >= POLAR_STRONG_MW) return "strong";
  if (g.frp_max >= POLAR_NOMINAL_MW) return "nominal";
  return "trace";
}

/**
 * Strongest MTG slot only. 10-minute geostationary frames are one stare, not
 * independent overpasses — summing every slot sent Iraq flares to p=1 Likely.
 */
export function bestMtgGroup(groups: SensorObservationGroup[]): SensorObservationGroup | null {
  let best: SensorObservationGroup | null = null;
  for (const g of groups) {
    if (g.platform !== "MTG") continue;
    if (!best) {
      best = g;
      continue;
    }
    const dw = thermalWeight(g) - thermalWeight(best);
    if (dw > 0 || (dw === 0 && g.frp_max > best.frp_max)) best = g;
  }
  return best;
}

/**
 * Heat that comes back for days without ever looking campaign-sized is a stack
 * or flare (ArcelorMittal Gent), not a wildfire. Two VIIRS seeing a furnace
 * every night is not Observed.
 */
export function recurringStaticHeat(groups: SensorObservationGroup[]): boolean {
  if (groups.length < 6) return false;
  const days = new Set(groups.map((g) => g.observed_at.slice(0, 10)));
  if (days.size < STANDING_MIN_DAYS) return false;
  for (const g of groups) {
    if (g.frp_max >= IMPORTANT_FRP_MW && g.n >= IMPORTANT_MIN_DETECTIONS) return false;
  }
  return true;
}

function thermalWeight(g: SensorObservationGroup): number {
  const s = thermalStrength(g);
  if (g.platform === "MTG") {
    if (s === "strong") return SCORE_WEIGHTS.mtg_strong;
    if (s === "nominal") return SCORE_WEIGHTS.mtg_nominal;
    return SCORE_WEIGHTS.mtg_weak;
  }
  if (s === "strong") return SCORE_WEIGHTS.thermal_strong;
  if (s === "nominal") return SCORE_WEIGHTS.thermal_nominal;
  if (s === "weak") return SCORE_WEIGHTS.thermal_weak;
  return SCORE_WEIGHTS.thermal_trace;
}

/** One unconfirmed look that went quiet — not the same as a polar revisit gap. */
function staleUnconfirmed(groups: SensorObservationGroup[], nowMs: number): EvidenceLine | null {
  if (groups.length === 0) return null;
  if (new Set(groups.map((g) => g.platform)).size >= 2) return null;
  const last = Math.max(...groups.map((g) => Date.parse(g.observed_at)));
  const ageH = (nowMs - last) / 3_600_000;
  if (ageH < 6) return null;
  const best = groups.reduce<ThermalTier>((acc, g) => {
    const s = thermalStrength(g);
    const rank = { trace: 0, weak: 1, nominal: 2, strong: 3 };
    return rank[s] > rank[acc] ? s : acc;
  }, "trace");
  // A strong polar pass can sit 12–18 h before the next overpass.
  if (best === "strong" && ageH < 24) return null;
  const hours = Math.max(1, Math.round(ageH));
  if (ageH >= 12) {
    return line("stale", `No second look in ${hours} h`, SCORE_WEIGHTS.stale_12h, "moderate");
  }
  return line("stale", `No second look in ${hours} h`, SCORE_WEIGHTS.stale_6h, "weak");
}

/** Same platform again within 10 / 30 min. One persist bonus per platform (the stronger gap). */
export function persistBonus(groups: SensorObservationGroup[]): { delta: number; label: string } | null {
  const byPlat = new Map<string, SensorObservationGroup[]>();
  for (const g of groups) {
    const list = byPlat.get(g.platform) ?? [];
    list.push(g);
    byPlat.set(g.platform, list);
  }
  let best = 0;
  let label = "";
  for (const [platform, list] of byPlat) {
    if (list.length < 2) continue;
    for (let i = 1; i < list.length; i++) {
      const dt = Date.parse(list[i]!.observed_at) - Date.parse(list[i - 1]!.observed_at);
      if (dt <= 10 * 60 * 1000 && SCORE_WEIGHTS.persist_10m > best) {
        best = SCORE_WEIGHTS.persist_10m;
        label = `${platform} persisted ≤10 min`;
      } else if (dt <= 30 * 60 * 1000 && SCORE_WEIGHTS.persist_30m > best) {
        best = SCORE_WEIGHTS.persist_30m;
        label = `${platform} persisted ≤30 min`;
      }
    }
  }
  return best > 0 ? { delta: best, label } : null;
}

/** Platforms that produced at least one non-trace look — traces do not confirm each other. */
function qualifyingPlatforms(groups: SensorObservationGroup[]): number {
  const plats = new Set<string>();
  for (const g of groups) {
    if (thermalStrength(g) !== "trace") plats.add(g.platform);
  }
  return plats.size;
}

/** One real look, not two — a second qualifying platform could make this Observed. */
export function canPromoteToObserved(groups: SensorObservationGroup[]): boolean {
  return qualifyingPlatforms(groups) === 1;
}

function independentBonus(platforms: number): { delta: number; label: string } | null {
  if (platforms < 2) return null;
  const extra = Math.max(0, platforms - 2);
  const delta = SCORE_WEIGHTS.independent_sensor + extra * SCORE_WEIGHTS.extra_sensor;
  return { delta, label: `${platforms} independent platforms` };
}

function frpBearing(groups: SensorObservationGroup[]): SensorObservationGroup[] {
  return groups.filter((g) => g.platform !== "S3" && g.frp_max > 0);
}

function frpGrew(groups: SensorObservationGroup[]): boolean {
  const thermal = frpBearing(groups);
  if (thermal.length < 2) return false;
  const first = thermal[0]!.frp_max;
  const last = thermal[thermal.length - 1]!.frp_max;
  return last >= 5 && first > 0 && last >= first * 1.3 && last - first >= 5;
}

function spatialGrew(groups: SensorObservationGroup[]): boolean {
  const thermal = frpBearing(groups);
  if (thermal.length < 2) return false;
  const last = thermal[thermal.length - 1]!;
  // 1 px → 2 px at 1 MW is not growth — the later look has to be a real one.
  if (thermalStrength(last) === "trace") return false;
  return last.n > thermal[0]!.n;
}

function contextLines(ctx: EventContext): EvidenceLine[] {
  const out: EvidenceLine[] = [];
  const fwi = saneFwi(ctx.fwi);
  if (fwi != null && fwi >= 50) {
    out.push(line("fwi", `Extreme FWI ${fwi.toFixed(0)}`, SCORE_WEIGHTS.fwi_extreme, "weak"));
  }
  const dry = (ctx.hours_since_rain != null && ctx.hours_since_rain >= 72) || (ctx.ffmc != null && ctx.ffmc >= 90);
  if (dry) out.push(line("dry", "Very dry fine fuel / long rain gap", SCORE_WEIGHTS.dry, "weak"));
  const windy = (ctx.wind_kmh != null && ctx.wind_kmh >= 25) || (ctx.wind_gust_kmh != null && ctx.wind_gust_kmh >= 50);
  if (windy) out.push(line("wind", "Strong wind / gusts", SCORE_WEIGHTS.wind, "weak"));
  if (ctx.human_m != null && ctx.human_m < 500) {
    out.push(line("human", `People within ${Math.round(ctx.human_m)} m`, SCORE_WEIGHTS.near_human, "weak"));
  }
  if (ctx.fuel === "water") out.push(line("water", "CORINE water", SCORE_WEIGHTS.water, "strong"));
  if (ctx.fuel === "industrial") {
    out.push(line("industrial", "CORINE industrial", SCORE_WEIGHTS.industrial_fuel, "strong"));
  }
  const hot = ctx.hot_m ?? ctx.solar_m;
  if (hot != null && hot < 500) {
    out.push(line("hot", `Static hot target within ${Math.round(hot)} m`, SCORE_WEIGHTS.static_hot, "strong"));
  }
  return out;
}

/**
 * Score the hour, not the calendar day. Dawn RH and a flat FWI do not count.
 * VPD peak is required before RH / gust / afternoon / grass can add.
 */
export function hourlyLines(ctx: EventContext): EvidenceLine[] {
  if (!dryEnough(ctx)) return [];
  // Percentile vs a cool night is not enough — VPD 1.8 kPa is not an ignition hour.
  const vpdPeak = ctx.vpd_pct != null && ctx.vpd_pct >= 0.85 && ctx.vpd_kpa != null && ctx.vpd_kpa >= 4;
  if (!vpdPeak) return [];

  const out: EvidenceLine[] = [
    line("hour_vpd", "VPD near 24–48 h peak", SCORE_WEIGHTS.hour_vpd_peak, "moderate"),
  ];
  if (ctx.rh_dry_pct != null && ctx.rh_dry_pct >= 0.85) {
    out.push(line("hour_rh", "RH among the driest hours", SCORE_WEIGHTS.hour_rh_dry, "weak"));
  }
  const gustPeak =
    (ctx.gust_pct != null && ctx.gust_pct >= 0.85) || (ctx.gust_jump != null && ctx.gust_jump >= 2.5);
  if (gustPeak) {
    out.push(line("hour_gust", "Gusts ramping vs morning / lookback", SCORE_WEIGHTS.hour_gust, "moderate"));
  }
  if (ctx.is_local_afternoon) {
    out.push(line("hour_afternoon", "Local afternoon on a peak-VPD hour", SCORE_WEIGHTS.hour_afternoon, "weak"));
  }
  if (ctx.fuel === "grass" || ctx.fuel === "shrub" || ctx.fuel === "crop") {
    out.push(line("fuel_fine", `Fine fuel (${ctx.fuel})`, SCORE_WEIGHTS.fuel_fine, "weak"));
  }
  return out;
}

/** Low SZA + modest ΔT is sunglint / hot ground, not a new ignition. */
function mtgQualityLines(groups: SensorObservationGroup[]): EvidenceLine[] {
  for (const g of groups) {
    if (g.platform !== "MTG") continue;
    const sza = g.sza_min;
    const dt = g.bt_diff_max ?? 0;
    if (sza != null && sza < 20 && dt < 6) {
      return [line("sunglint", `MTG low SZA ${sza.toFixed(0)}° + modest ΔT`, SCORE_WEIGHTS.sunglint, "moderate")];
    }
  }
  return [];
}

function thermalLabel(g: SensorObservationGroup, s: ThermalTier): string {
  const bits = [`${g.platform} ${s} group (${g.n} px, FRP ${g.frp_max.toFixed(1)} MW`];
  if (g.bt_diff_max != null) bits.push(`ΔT ${g.bt_diff_max.toFixed(1)} K`);
  if (g.sza_min != null) bits.push(`SZA ${g.sza_min.toFixed(0)}°`);
  return `${bits.join(", ")})`;
}

/**
 * Still-burning score. Decays with silence; cannot exceed fire_probability.
 * Polar gaps of a few hours are normal; a day of silence is not.
 */
export function scoreActive(
  groups: SensorObservationGroup[],
  fireProbability: number,
  nowMs: number,
): number {
  if (groups.length === 0) return 0;
  const thermal = frpBearing(groups);
  const lastLook = thermal[thermal.length - 1] ?? groups[groups.length - 1]!;
  const last = Date.parse(lastLook.observed_at);
  const ageH = Math.max(0, (nowMs - last) / 3_600_000);
  let logit = logitFromP(Math.min(0.85, fireProbability)) - 0.12 * ageH;
  if (ageH < 2) logit += 0.8;
  if (ageH > 20) logit -= 2.0;
  if (frpGrew(groups)) logit += 0.4;
  return Math.min(fireProbability, sigmoid(logit));
}

export function scoreGroups(
  groups: SensorObservationGroup[],
  context: EventContext,
  nowMs: number,
): EventScore {
  const evidence: EvidenceLine[] = [
    line("prior", groups.length ? "Base prior" : "Base prior (no thermal yet)", SCORE_WEIGHTS.prior_base, "weak"),
  ];
  let logit = SCORE_WEIGHTS.prior_base;
  const hourly = hourlyLines(context);
  for (const e of [...contextLines(context), ...hourly]) {
    logit += e.delta_logit;
    evidence.push(e);
  }

  // Polar overpasses hours apart stay independent. MTG 10-min slots do not.
  const bestMtg = bestMtgGroup(groups);
  for (const g of groups) {
    if (g.platform === "MTG" && g !== bestMtg) continue;
    const w = thermalWeight(g);
    const s = thermalStrength(g);
    logit += w;
    evidence.push(
      line(
        `thermal:${g.platform}:${g.pass_bucket}`,
        thermalLabel(g, s),
        w,
        s === "trace" || s === "weak" ? "moderate" : "strong",
      ),
    );
  }
  for (const e of mtgQualityLines(groups)) {
    logit += e.delta_logit;
    evidence.push(e);
  }

  const persist = persistBonus(groups);
  if (persist) {
    logit += persist.delta;
    evidence.push(line("persist", persist.label, persist.delta, "moderate"));
  }

  const platforms = new Set(groups.map((g) => g.platform)).size;
  const qualified = qualifyingPlatforms(groups);
  const indep = independentBonus(qualified);
  if (indep) {
    logit += indep.delta;
    evidence.push(line("independent", indep.label, indep.delta, "strong"));
  }

  if (frpGrew(groups)) {
    logit += SCORE_WEIGHTS.frp_growth;
    evidence.push(line("frp_growth", "FRP rising across groups", SCORE_WEIGHTS.frp_growth, "moderate"));
  }
  if (spatialGrew(groups)) {
    logit += SCORE_WEIGHTS.spatial_growth;
    evidence.push(line("spatial", "Later pass covers more pixels", SCORE_WEIGHTS.spatial_growth, "weak"));
  }

  const stale = staleUnconfirmed(groups, nowMs);
  if (stale) {
    logit += stale.delta_logit;
    evidence.push(stale);
  }

  // Daily drought stays hidden. A peak hour may reach Suspected; class locks stop Likely.
  if (groups.length === 0 && hourly.length === 0) {
    logit = Math.min(logit, logitFromP(NO_THERMAL_DAY_CAP));
  }

  const standing = recurringStaticHeat(groups);
  if (standing) {
    const days = new Set(groups.map((g) => g.observed_at.slice(0, 10))).size;
    const recur = line(
      "static_recur",
      `Heat returned on ${days} days without a campaign-sized look`,
      SCORE_WEIGHTS.static_hot,
      "strong",
    );
    logit += recur.delta_logit;
    evidence.push(recur);
  }

  let fire_probability = sigmoid(logit);
  let event_status = classifyEventStatus(fire_probability, groups.length > 0, qualified);
  // Class lock — stacked polar revisits still swamp a −3.5 penalty.
  if (standing && (event_status === "confirmed" || event_status === "likely")) {
    event_status = "suspected";
    fire_probability = Math.min(fire_probability, EVENT_THRESHOLDS.likely);
  }
  const active_probability = scoreActive(groups, fire_probability, nowMs);
  return {
    fire_probability,
    active_probability,
    event_status,
    logit,
    evidence,
    thermal_groups: groups.length,
    independent_sensors: platforms,
  };
}

export function scoreIncident(members: Detection[], context: EventContext, nowMs: number): EventScore {
  return scoreGroups(groupObservations(members), context, nowMs);
}

/** Copy score fields onto an incident. Intensity `status` is unchanged. */
export function applyScore(
  incident: Incident,
  members: Detection[],
  context: EventContext,
  nowMs: number,
): Incident {
  const score = scoreIncident(members, context, nowMs);
  return {
    ...incident,
    fire_probability: score.fire_probability,
    active_probability: score.active_probability,
    event_status: score.event_status,
    evidence: score.evidence,
  };
}
